import {
  performReorderMainSections,
  performReorderSectionQuestions,
  performReorderSubSections,
  resolveMainSectionReorderContext,
  resolveQuestionReorderContext,
  resolveSubSectionReorderContext,
} from './create_sections_reorder_operation_helpers.js';

export function resolveMainSectionId(main_section, fallback_index = null) {
  const raw_main_section_id = main_section?.main_section_id ?? main_section?.id ?? main_section?.backend_id ?? fallback_index ?? null;
  const main_section_id = Number(raw_main_section_id);
  return main_section_id;
}

export function resolveSubSectionId(sub_section, fallback_index = null) {
  const raw_sub_section_id = sub_section?.section_id ?? sub_section?.backend_id ?? sub_section?.id ?? fallback_index ?? null;
  const sub_section_id = Number(raw_sub_section_id);
  return sub_section_id;
}

export function resolveQuestionId(question, fallback_index = null) {
  const raw_question_id = question?.question_id ?? question?.backend_id ?? question?.id ?? fallback_index ?? null;
  const question_id = Number(raw_question_id);
  return question_id;
}

function isValidIndex(index, items) {
  return index >= 0 && index < items.length;
}

function areDistinctIndices(source_index, target_index) {
  return source_index !== target_index;
}

export function isValidReorderRange(source_index, target_index, items) {
  return areDistinctIndices(source_index, target_index)
    && isValidIndex(source_index, items)
    && isValidIndex(target_index, items);
}

export function moveItemByIndex(items, source_index, target_index) {
  const reordered_items = [...items];
  const [moved_item] = reordered_items.splice(source_index, 1);
  if (!moved_item) {
    return null;
  }

  reordered_items.splice(target_index, 0, moved_item);
  return {
    moved_item,
    reordered_items,
  };
}

export function buildMainSectionReorderPayload(form_id, reordered_sections) {
  const payload = {
    form_id: form_id,
    reordered_main_sections: reordered_sections.map((item, index) => {
      return {
        main_section_id: resolveMainSectionId(item),
        order_number: index + 1,
      };
    }),
  };
  return payload;
}

function buildMainSectionDropState(ctx, target_main_section) {
  const target_main_section_id = resolveMainSectionId(target_main_section);
  const should_skip = !target_main_section_id
    || Number(ctx.dragged_main_section.main_section_id) === target_main_section_id;
  return { should_skip, target_main_section_id };
}

export async function handleMainSectionDrop(ctx, target_main_section, target_index) {
  const drop_state = buildMainSectionDropState(ctx, target_main_section);
  if (drop_state.should_skip) {
    ctx.dragged_main_section = null;
    return;
  }

  await ctx.reorder_main_sections_by_index(ctx.dragged_main_section.main_section_index, target_index);
  ctx.dragged_main_section = null;
}

function validateMainSectionReorderContext(ctx, reorder_context) {
  if (!reorder_context.form_id) {
    ctx.show_error('Please select a valid form section first.');
    return false;
  }

  if (!reorder_context.has_valid_range) {
    return false;
  }

  return true;
}

async function withReorderLoader(ctx, flag_key, message, action) {
  ctx[flag_key] = true;
  const loader = await ctx.show_loader(message);
  try {
    await action();
  } finally {
    await ctx.close_loader(loader);
    ctx[flag_key] = false;
  }
}

function buildMainSectionReorderPayloadState(reorder_context, source_index, target_index) {
  const moved_result = moveItemByIndex(
    reorder_context.ordered_sections,
    source_index,
    target_index
  );
  const payload_state = {
    moved_result,
    payload: moved_result
      ? buildMainSectionReorderPayload(
        reorder_context.form_id,
        moved_result.reordered_items
      )
      : null,
  };
  return payload_state;
}

function resolveMainSectionReorderPayloadState(ctx, source_index, target_index) {
  const reorder_context = resolveMainSectionReorderContext(
    ctx,
    source_index,
    target_index,
    isValidReorderRange
  );
  if (!validateMainSectionReorderContext(ctx, reorder_context)) {
    return null;
  }

  const payload_state = buildMainSectionReorderPayloadState(
    reorder_context,
    source_index,
    target_index
  );
  if (!payload_state.moved_result) {
    return null;
  }

  return payload_state;
}

export async function reorderMainSectionsByIndex(ctx, source_index, target_index) {
  const payload_state = resolveMainSectionReorderPayloadState(
    ctx,
    source_index,
    target_index
  );
  if (!payload_state) {
    return;
  }
  await withReorderLoader(ctx, 'reordering_main_sections', 'Reordering main sections...', async () => {
    await performReorderMainSections(ctx, payload_state.payload);
  });
}

function buildSubSectionDropState(ctx, main_section, target_sub_section) {
  const current_main_section_id = resolveMainSectionId(main_section);
  const target_section_id = resolveSubSectionId(target_sub_section);
  const matches_main_section = Number(ctx.dragged_sub_section.main_section_id) === current_main_section_id;
  const is_same_section = Number(ctx.dragged_sub_section.section_id) === target_section_id;
  const should_skip = !current_main_section_id || !matches_main_section || !target_section_id || is_same_section;
  return {
    current_main_section_id,
    should_skip,
  };
}

export async function handleSubSectionDrop(ctx, main_section, target_sub_section, target_index) {
  const drop_state = buildSubSectionDropState(ctx, main_section, target_sub_section);
  if (drop_state.should_skip) {
    ctx.dragged_sub_section = null;
    return;
  }

  await ctx.reorder_sub_sections_by_index(main_section, ctx.dragged_sub_section.sub_section_index, target_index);
  ctx.dragged_sub_section = null;
}

function validateSubSectionReorderContext(ctx, reorder_context) {
  const has_valid_section = Boolean(
    reorder_context.form_main_section_id
    && reorder_context.moved_result
    && reorder_context.form_section_id
  );
  if (!has_valid_section) {
    ctx.show_error('Please select a valid sub section first.');
    return false;
  }

  return true;
}

async function runSubSectionReorder(ctx, reorder_context, target_index) {
  await withReorderLoader(ctx, 'reordering_sub_sections', 'Reordering sub sections...', async () => {
    await performReorderSubSections(ctx, reorder_context, target_index);
  });
}

function resolveValidSubSectionReorderContext(ctx, main_section, source_index, target_index) {
  const reorder_context = resolveSubSectionReorderContext(
    main_section,
    source_index,
    target_index,
    isValidReorderRange,
    moveItemByIndex,
    resolveMainSectionId,
    resolveSubSectionId
  );

  if (!validateSubSectionReorderContext(ctx, reorder_context)) {
    return null;
  }

  return reorder_context;
}

export async function reorderSubSectionsByIndex(ctx, main_section, source_index, target_index) {
  const reorder_context = resolveValidSubSectionReorderContext(
    ctx,
    main_section,
    source_index,
    target_index
  );
  if (!reorder_context) {
    return;
  }
  await runSubSectionReorder(ctx, reorder_context, target_index);
}

function buildQuestionDropState(ctx, section, target_question, target_index) {
  const source_section_id = Number(ctx.dragged_question.section_id);
  const target_section_id = resolveSubSectionId(section);
  const target_question_id = resolveQuestionId(target_question);
  const is_same_question = ctx.dragged_question.question_index === target_index;
  const should_skip = !source_section_id
    || source_section_id !== target_section_id
    || !target_question_id
    || is_same_question;
  return { should_skip };
}

function validateQuestionReorderContext(ctx, reorder_context) {
  if (reorder_context.moved_result && reorder_context.section_question_id) {
    return true;
  }

  if (!reorder_context.section_question_id && reorder_context.moved_result) {
    ctx.show_error('Could not find the selected question mapping.');
  }
  return false;
}

function resolveValidQuestionReorderContext(ctx, section, source_index, target_index) {
  const reorder_context = resolveQuestionReorderContext(
    section,
    source_index,
    target_index,
    isValidReorderRange,
    moveItemByIndex
  );

  if (!validateQuestionReorderContext(ctx, reorder_context)) {
    return null;
  }

  return reorder_context;
}

export async function handleQuestionDrop(ctx, section, target_question, target_index) {
  const drop_state = buildQuestionDropState(ctx, section, target_question, target_index);
  if (drop_state.should_skip) {
    ctx.dragged_question = null;
    return;
  }

  await ctx.reorder_section_questions(section, ctx.dragged_question.question_index, target_index);
  ctx.dragged_question = null;
}

export async function reorderSectionQuestions(ctx, section, source_index, target_index) {
  const reorder_context = resolveValidQuestionReorderContext(
    ctx,
    section,
    source_index,
    target_index
  );
  if (!reorder_context) {
    return;
  }
  await runQuestionReorder(ctx, section, reorder_context, target_index);
}

async function runQuestionReorder(ctx, section, reorder_context, target_index) {
  await withReorderLoader(ctx, 'reordering_section_questions', 'Reordering questions...', async () => {
    await performReorderSectionQuestions(ctx, section, reorder_context, target_index);
  });
}
